<script>
/* Vue native methods */
import { computed, inject, onBeforeMount, onMounted, reactive, ref, watch, watchEffect, nextTick } from 'vue';
/* Vue router */
import { useRouter } from 'vue-router';

/* Component methods */
import { checkDiscountCodeApi } from './DiscountCodeComponent.vue';

/* Axios imports */
import axios_native from 'axios';
import axios from '../config/axios';

/* Data Strings */
import tooltipData from '../strings/tooltip-data';
import tooltipDataWl from '../strings/tooltip-data-wl';

/* Components */
import InputChoice from './InputChoice';
import ServiceTable from './ServiceTable';
import OnlineOrder from './OnlineOrder';
import ServiceTableMobile from './ServiceTableMobile';
import ServiceTableTablet from './ServiceTableTablet.vue';
import HeaderComponent from './HeaderComponent';
import MobileHeaderComponent from './MobileHeaderComponent.vue';
import FooterComponent from './FooterComponent';
import FormComponent from './FormComponent.vue';
import DateTimePickerComponent from './DateTimePickerComponent.vue';
import StatisticsComponent from './StatisticsComponent.vue';
import Multiselect from '@vueform/multiselect';
import Toggle from '@vueform/toggle';

/* Helper functions */
import ApiHelper from '../helpers/apiHelper';
import { handleZipInput, handleZipFocus } from '../helpers/zipInputHelper';
import HeaderLogo from './HeaderLogo.vue';

/* Whitelabel configs */
const whitelabel = require('../../whitelabel.config')[BRAND_THEME];

import { usePackagePriceStore } from '../stores/packagePriceStore';
import { useAppDataStore } from '../stores/appDataStore';

export default {
  components: {
    HeaderLogo,
    InputChoice,
    ServiceTable,
    OnlineOrder,
    ServiceTableMobile,
    ServiceTableTablet,
    HeaderComponent,
    MobileHeaderComponent,
    FooterComponent,
    FormComponent,
    DateTimePickerComponent,
    StatisticsComponent,
    Multiselect,
    Toggle
  },
  props: {},
  setup() {
    const packagePriceStore = usePackagePriceStore();
    const appDataStore = useAppDataStore();
    const appData = computed(() => appDataStore.appData);

    const firstFormComponent = ref(null);
    const installationFormComponent = ref(null);
    const dateTimePickerComponent = ref(null);

    const router = useRouter();
    const landingPageWhiteLabels = ref([ // White labels that have landing page
      'default',
      'corporatebenefits',
      'liftstar',
      'careship',
      'landingpage-bawue-messe',
      'hanova',
      'hausnotruf-testen',
      'enercity'
    ]);

    const piwikWhiteLabels = ref([ // White labels that send data to Piwik
      'default',
      'corporatebenefits',
      'liftstar',
      'careship',
      'hausnotruf-testen'
    ]);

    const gtmWhiteLabels = ref([ // White labels that send data to GTM
      'default',
      'liftstar',
      'careship',
      'hausnotruf-testen'
    ]);

    const globalDiscountAvailable = inject('discountAvailable');
    let stepHistory = reactive([1]); // Array of steps for full history on how user has navigated trough steps using the thermometer
    let gtmPreviousStep = ref(null); // 'PreviouStep' property for GTM
    let windowWidth = ref(window.innerWidth);

    // Fetch GET params from URL
    const queryObject = Object.fromEntries(new URLSearchParams(window.location.search));

    const stepTransitionDuration = ref(100);
    const app_theme_brand = ref(BRAND_THEME);
    const app_title = ref(whitelabel.title);
    const custom_brand = whitelabel.brand;

    let appPackages = ref({
      packages: {},
      comment: ''
    });

    const reversedAppPackages = ref({
      packages: {}
    });

    const localStorageDiscount = ref(`discount_code_applied_1`);

    let appliedDiscountCode = ref(
      JSON.parse(localStorage.getItem(localStorageDiscount.value)) || null
    );

    const previousStep = ref(1);

    const steps = reactive([{ id: 1 }, { id: 2 }, { id: 3 }, { id: 4 }, { id: 5 }, { id: 6 }, { id: 7 }]);
    const meetingTimeSteps = reactive([{ id: 1 }, { id: 2 }, { id: 3 }]);

    const tooltipContent = ref(tooltipData);
    const tooltipContentWl = ref(tooltipDataWl);

    const appRadios = ref([
      'Empfehlung',
      'Anzeige',
      'Suchmaschine',
      'Flyer',
      'Mailing',
      'TV',
      'Wartezimmer TV',
      'Social Media',
      'Radio',
      'E-Mail'
    ]);

    const appRadiosBurda = ref([
      'Freizeit Revue',
      'Freizeit Spass',
      'Viel Spaß',
      'Frau Im Trend',
      'SUPERillu',
      'Guter Rat',
      'my life',
      'Netdoktor.de'
    ]);

    const packagePricesDiscounts = ref(null);

    let formErrorMessage = ref('');
    let validationErrors = ref([]);

    let intervalsNotForApi = ref([]);
    let intervalExceptionsNotForApi = ref([]);

    /********************************************************************************************************
    /**************************************** COMPUTED ******************************************************
    /********************************************************************************************************/

    const isTabletPortrait = computed(() => {
      return windowWidth.value >= 768 && windowWidth.value <= 1024 && screen.orientation.type === 'portrait-primary';
    });

    const isMobile = computed(() => {
      return windowWidth.value <= 768;
    });

    const filteredAppRadios = computed(() => {
      if (
        BRAND_THEME === "default" ||
        BRAND_THEME === "hausnotruf-testen" ||
        BRAND_THEME === "liftstar" ||
        BRAND_THEME === "careship"
      ) {
        return appRadios.value;
      } else if (BRAND_THEME === "burda") {
        return appRadiosBurda.value;
      }
      return false;
    });

    /********************************************************************************************************
    /**************************************** METHODS *******************************************************
    /********************************************************************************************************/

    const handleFormSubmit = async () => {
      let version = BRAND_THEME == 'seniorenportal' ? 'version=faz' : 'version=default';
      let selectedPackage = appData.value.s5.value == 'basis_plus' ? 'Basis-plus' : appData.value.s5.value;

      if (appData.value.s4.value == 'Zuhause und unterwegs' && appData.value.s5.value.indexOf('-Kombinotruf') === -1) {
        selectedPackage = appData.value.s5.value + '-Kombinotruf';
      }

      let land = '&landesverband=' + encodeURIComponent(appData.value.contact.region);
      let postUrl = `${__API_URL__}/submit?${version}${land}`;

      let Bemerkung = appData.value.appPackages?.comment + '; ' + appData.value.orderType;
      let Verfugbarkeitsquelle = '';
      let Datum = '';
      let meetingTimeFrom = '';
      let meetingTimeTo = '';
      let meetingType = '';
      let meetingReserved = 'FALSCH';
      let preferedMeetingDate = 'FALSCH';
      let Wunschzeitfenster = '';

      if (appData.value.contact.termin_select) {
        meetingReserved = 'WAHR';
        Datum = appData.value.s7.value.substring(0, 10);

        meetingTimeFrom = appData.value.s7.meeting_time.substring(0, 5);
        meetingTimeTo = appData.value.s7.meeting_time.substring(8, 13);

        meetingType = 'physisch';
        Verfugbarkeitsquelle = 'Standardliste';
        preferedMeetingDate = appData.value.contact.meeting_times_input;

        if (appData.value.contact.meeting_times_input == 'WAHR') {
          meetingReserved = 'FALSCH';
          Wunschzeitfenster = appData.value.s7.value + ', ' + appData.value.s7.meeting_time;
        }
      }

      let installationAddress = appData.value.installation_address_checkbox ? 'FALSCH' : 'WAHR';

      if (appData.value.orderType === 'Unverbindliche Anfrage') {
        installationAddress = 'FALSCH';
      }

      let zipOfContact = '';

      if (appData.value.contact.zipOfContact) {
        zipOfContact = appData.value.contact.zipOfContact?.value?.postcode || '';
      }

      // This can happen if user clicks submit while still entering the zipcode
      if (!zipOfContact || zipOfContact.length < 5) {
        validationErrors.push('zipOfContact');
        firstFormComponent.value.$el.scrollIntoView({ behavior: 'smooth' });
        return;
      }

      if (!zipOfContact || zipOfContact.length < 5) {
        // If zipcode stiill does not match requirements or is empty use step 2 zipcode
        // This should never happen
        zipOfContact = appData.value.contact.zip;
      }

      let installationAddressZipOfContact = '';

      if (appData.value.contact.installation_address_zipOfContact) {
        installationAddressZipOfContact = appData.value.contact.installation_address_zipOfContact?.value?.postcode;
      }

      // This can happen if user clicks submit while still entering the zipcode
      if (installationFormComponent.value && (!installationAddressZipOfContact || installationAddressZipOfContact.length < 5)) {
        validationErrors.push('installation_address_zipOfContact');
        installationFormComponent?.value?.$el.scrollIntoView({ behavior: 'smooth' });
        return;
      }

      if ((!installationAddressZipOfContact || installationAddressZipOfContact.length < 5) && installationAddress == 'WAHR') {
        // If zipcode stiill does not match requirements or is empty use step 2 zipcode
        // This should never happen
        installationAddressZipOfContact = appData.value.contact.zip;
      }

      firstFormComponent.value.validateAllFields();

      let installationAddressPhone = appData.value.contact.installation_address_phone || '';

      // Prices needed for the KCS email JSON file [JDB]
      let userPrice = appData.value.s5.userSelectedPrice;

      // Preis_Selbstzahler
      let selfPayerPrice = userPrice;

      // Preis_Zuzahlunglet
      let additionalPaymentPrice = 27.00;

      // Zuzahlungsfähig
      let eligibleForAdditionalPayment = appData.value.s3.value == 'Ja';

      if (appData.value.s3.value != 'Ja') {
        additionalPaymentPrice = 0;
      }

      userPrice += additionalPaymentPrice;

      let mailBodyData = {
        Kontaktaufnahme: 'sofort',
        Servicecenter: appData.value.contact.region,
        PLZ_Ort: appData.value.contact.zip + ', ' + appData.value.contact.city,
        Ort: appData.value.contact.city,
        Hausnotruftyp: appData.value.s4.value,
        Nutzer: appData.value.s1.value,
        Pflegegrad: appData.value.s3.value,
        Angebot: selectedPackage,
        discount_code: appliedDiscountCode.value,
        self_payer_price: selfPayerPrice,
        additional_payment_price: additionalPaymentPrice,
        user_price: userPrice,
        eligible_for_additional_payment: eligibleForAdditionalPayment,
        partnertarif: appData.value.partnertarif,
        rabatte: getDiscountData(),
        kampagne: appData.value.usersource,
        Quelle: custom_brand,
        queryId: appData.value.queryId,
        Uhrzeit_von: meetingTimeFrom,
        Uhrzeit_bis: meetingTimeTo,
        Datum: Datum,
        Termin_gebucht: meetingReserved,
        Art_des_Termins: meetingType,
        Verfügbarkeitsquelle: Verfugbarkeitsquelle,
        Daten_Wunschtermin: preferedMeetingDate,
        Wunschzeitfenster: Wunschzeitfenster,
        Verfuegbare_Pakete: packageList(),
        Services_Potenziale: getPotentialServices(appData.value.s5.value),
        Bemerkung: Bemerkung,
        jsonbemerkung: appData.value.appPackages?.comment,
        Order_type: appData.value.orderType,
        Anrede: appData.value.contact.anrede,
        Name: appData.value.contact.lastname,
        Vorname: appData.value.contact.firstname,
        Adresse: appData.value.contact.street?.value,
        Hausnummer: appData.value.contact.house_number,
        PLZ_Ansprechpartner: zipOfContact,
        Stadt: appData.value.contact.stadt,
        Vorwahl: appData.value.contact.phone_prefix,
        Telefon: appData.value.contact.phone,
        Email: appData.value.contact.email,
        Anrede_Kunde: appData.value.contact.installation_address_anrede,
        Vorname_Kunde: appData.value.contact.installation_address_firstname,
        Nachname_Kunde: appData.value.contact.installation_address_lastname,
        Adresse_Kunde: appData.value.contact.installation_address_street?.value || '',
        Hausnummer_Kunde: appData.value.contact.installation_address_house_number,
        PLZ_Kunde: installationAddressZipOfContact,
        Ort_Kunde: appData.value.contact.installation_address_stadt,
        Telefon_Kunde: installationAddressPhone,
        Vorwahl_Kunde: appData.value.contact.installation_address_phone_prefix,
        Email_Kunde: appData.value.contact.installation_address_email,
        Daten_Kunde: installationAddress,
        'radio-erfahren': appData.value.erfahren,
        Datenschutz: appData.value.agree,
        meeting_times_api: appData.value.contact.meeting_times_api,
        meeting_times_api_ts: appData.value.contact.meeting_times_api_ts
      };

      if (appData.value.contact.termin_select) {

        let intervals = [];
        intervals.push({
          'date': appData.value.s7.value,
          'startTime': appData.value.s7.meeting_time.substring(0, 5) + ':00',
          'endTime': appData.value.s7.meeting_time.substring(8, 13) + ':00',
          'capacity': appData.value.s7.capacity
        });

        let intervalsJSON = JSON.stringify(intervals);
        let config = {
          params: {
            zipcode: appData.value.contact.zip,
            place: appData.value.contact.city,
            interval: intervalsJSON
          }
        };

        clearDatepicker();

        // In production environment double check if selected meeting time is available
        if (INST !== 'dev') {
          const apiHelper = new ApiHelper('https://www.johanniterhausnotruf.de/mt.php', 'POST', config);
          const intervalResponse = await apiHelper.fetchData();

          if (intervalResponse.data.intervalls && intervalResponse.data.intervalls.length === 0) {
            formErrorMessage.value = 'Die von Ihnen gewählte Uhrzeit ist nicht mehr verfügbar. Bitte wählen Sie eine andere Uhrzeit.';
            return;
          }
        }
      }

      const apiHelper = new ApiHelper(postUrl, 'POST', mailBodyData);
      const response = await apiHelper.fetchData();

      let redirect_url = response.data?.redirect_url;

      if (redirect_url) {
        redirect_url += '?s1=' + appData.value.s1.intValue + '&s3=' + appData.value.s3.intValue + '&s4=' + appData.value.s4.intValue
          + '&s5=' + appData.value.s5.mappedPackages[appData.value.s5.value];

        if (appData.value.cid) {
          redirect_url = redirect_url + '&cid=' + appData.value.cid;
        }

        // Clear stored data
        localStorage.clear();
        window.top.location.href = redirect_url;
      }
    };

    // Gets the discount data needed for KCS JSON file [JDB] - Rabatte
    const getDiscountData = () => {
      if (BRAND_THEME === 'corporatebenefits') {
        return [
          {
            Rabatt_typ: 30,
            Rabatt_code: appliedDiscountCode.value,
            Dauer_Rabatt: 0,
            Wert: 10
          }
        ];
      }
      if (appData.value.appPackages.couponDiscountAvailable) {
        return [
          {
            'Rabatt_typ': 10,
            'Rabatt_code': 'Sicherheitswochen_Anschlussgebühr',
            'Dauer_Rabatt': null,
            'Wert': 49
          },
          {
            'Rabatt_typ': 20,
            'Rabatt_code': 'Sicherheitswochen_Monatsgebühr',
            'Dauer_Rabatt': 4,
            'Wert': 20
          }
        ];
      }
    };
    // Selects all packages that are more expensive that the one which user as currently selected needed for KCS JSON file [JDB]
    const getPotentialServices = (selectedPackage = 'basis') => {
      const { packages } = appData.value.appPackages;
      const selectedPackagePrice = appData.value.s5.userSelectedPrice || 0;
      const additionalPaymentPrice = appData.value.s3.value == 'Ja' ? 27.00 : 0;

      const artikel = Object.entries(packages)
        .filter(([pack, packData]) => pack !== selectedPackage && packData?.price != null && packData.price > selectedPackagePrice)
        .map(([pack, packData]) => ({
          Name: pack,
          Produkteigenschaften: [
            {
              'Artikelnummer': '',
              'Zuzahlungsfähig': 'true'
            }
          ],
          Preis_Selbstzahler: packData?.price,
          Preis_Zuzahlung: additionalPaymentPrice,
          Preis_gesamt: packData?.price + additionalPaymentPrice
        }));

      return { artikel };
    };

    const sendDataToPiwik = () => {
      let stepLabel = appData.value.cid ? 'step' + appData.value.currentStep : 'step' + appData.value.currentStep + '_test';
      let value = appData.value.cid ? appData.value.cid : '';

      pushToDataLayer('event', stepLabel, value);
    };
    // Appends the currently active step to the URL as param ?schritt
    const appendStepToUrl = () => {
      const query = { schritt: appData.value.currentStep };
      router.push({ query });
    };

    // Map each step answer data to appData.value values
    const mapAnswerValues = (data) => {
      appData.value[data.step].value = data.value;
      appData.value[data.step].intValue = data.intValue;
    };

    const handleValidationErrors = (errors) => {
      validationErrors = errors;
    };
    // Clear date & time picker values
    const clearDatepicker = () => {
      appData.value.s7.value = '';
      appData.value.contact.termin_select = false;
      dateTimePickerComponent.value.closePopup();
    };

    const pushToDataLayer = (label, event, value) => {
      dataLayer.push({ [label]: event, 'cid': value });
    };

    const reverseAppPackages = () => {
      appData.value.reversedAppPackages.packages = {};

      Object.keys(appData.value.appPackages.packages)
        .reverse()
        .forEach((key) => {
          if (appData.value.appPackages.packages[key]) {
            appData.value.reversedAppPackages.packages[key] = appData.value.appPackages.packages[key];
          }
        });

      return appData.value.reversedAppPackages.packages;
    };

    const getPackages = async () => {
      let apiUrl =
        `${__API_URL__}/services?id=${appData.value.contact.servicesID}&insurance=${appData.value.s3.value == 'Ja'}&mobile=${appData.value.s4.value == 'Zuhause und unterwegs'}`;

      appData.value.contact.meeting_times = '';
      const apiHelper = new ApiHelper(apiUrl, 'GET', '');
      const packageDataResponse = await apiHelper.fetchData();

      if (packageDataResponse.status !== 200 && packageDataResponse.status !== 204) {
        console.error('Fetching packages failed: ', packageDataResponse.status);
        return;
      }

      appData.value.appPackages = packageDataResponse.data.data;

      if (appData.value.appPackages.comment?.includes('Basis plus')) {
        let basis_plus = {
          price: 45
        };

        appData.value.appPackages.packages = Object.assign({ basis_plus: {} }, appData.value.appPackages.packages);
        appData.value.appPackages.packages.basis_plus.price = appData.value.s3.value == 'Ja' ? 18 : 45;

        if (appData.value.s4.value == 'Zuhause und unterwegs') {
          appData.value.appPackages.packages.basis_plus.price = appData.value.s3.value == 'Ja' ? 32 : 59;
        }
      }

      if (appData.value.appPackages.packages.basis == null) {
        appData.value.appPackages.packages.basis = Object.assign({ price: 23 }, appData.value.appPackages.packages.basis);
        appData.value.appPackages.packages.basis.price = appData.value.s3.value == 'Ja' ? 23 : 25;

        if (appData.value.s4.value == 'Zuhause und unterwegs') {
          appData.value.appPackages.packages.basis.price = appData.value.s3.value == 'Ja' ? 33 : 35;
        }

      }

      appData.value.contact.region = appData.value.appPackages.landesverband;
      appData.value.contact.meeting_times = appData.value.appPackages.meeting_times;
      appData.value.contact.public_holidays = appData.value.appPackages.public_holidays;

      reverseAppPackages();

    };
    // Handle zipcode API request each time user updates zipcode field value
    const handleACChange = async (data) => {
      if (data.length < 2) {
        return;
      }

      try {
        const apiUrl = `${__API_URL__}/zipcodes?zip=${data}`;
        const apiHelper = new ApiHelper(apiUrl, 'GET', '');
        const response = await apiHelper.fetchData();

        if (response.status !== 200 && response.status !== 204) {
          console.error('Fetching zipcodes failed: ', response.status);
          return;
        }

        // Map the response in to the format that is needed for autocomplete component
        appData.value.s2.options = response.data.data.map((item) => ({
          value: {
            id: item.id,
            zip: item.zip,
            city: item.city
          }
        }));
      } catch (error) {
        console.error('Error handling AC select:', error);
      }

      appData.value.s2.value = data;
      appData.value.s2.zipCodeErrorVisible = false;
    };
    // Handle zipcode selection from the dropdown
    const handleACSelect = async () => {
      appData.value.s2.zipCodeErrorVisible = false;
      appData.value.contact.zip = appData.value.s2?.value?.zip || appData.value.s2.value?.value.zip;
      appData.value.contact.city = appData.value.s2?.value?.city || appData.value.s2.value?.value.city;
      appData.value.contact.servicesID = appData.value.s2?.value?.id || appData.value.s2.value?.value.id;

      await getPackages();

      appData.value.contact.meeting_times_api = '';
      appData.value.s3.value = '';
      appData.value.s4.value = '';
      appData.value.s5.value = '';
      appData.value.s7.value = '';
      appData.value.contact.termin_select = false;

      // Handle meeting time intervals & exceptions for step 6 datepicker
      if (appData.value.contact.meeting_times) {
        const intervalsJSON = getIntervalExceptions();

        let config = {
          params: {
            zipcode: appData.value.contact.zip,
            place: appData.value.contact.city,
            interval: intervalsJSON
          }
        };

        const apiHelper = new ApiHelper('https://www.johanniterhausnotruf.de/mt.php', 'POST', config);
        const intervalExceptionsResponse = await apiHelper.fetchData();

        if (intervalExceptionsResponse.status !== 200 && intervalExceptionsResponse.status !== 204) {
          console.error('Fetching interval exceptions failed: ', intervalExceptionsResponse.status);
          return;
        }

        appData.value.contact.meeting_times_api = intervalExceptionsResponse.data['intervalls'];

        // Add back the interval exceptions that we do not send to API
        // Extracted from the initial data on step 2
        appData.value.meeting_times_exceptions_not_api = intervalExceptionsNotForApi.value;

        appData.value.queryId = intervalExceptionsResponse.data['queryId'];

        // Get year, month, and day part from the date
        let date = new Date();
        let year = date.toLocaleString('default', { year: 'numeric' });
        let month = date.toLocaleString('default', { month: '2-digit' });
        let day = date.toLocaleString('default', { day: '2-digit' });

        appData.value.contact.meeting_times_api_ts = year + '-' + month + '-' + day;
      }

    };
    // Handle what happens when user clicks or taps out of the zipcode options dropdown
    const handleACClose = () => {
      appData.value.s2.zipCodeErrorVisible = false;

      // If there is only one option left then autoselect it
      if (appData.value.s2.options.length == 1) {
        appData.value.s2.value = appData.value.s2.options[0];
        handleACSelect();
      }
      // If there are two options left show error
      if (appData.value.s2.value.length == 5 && appData.value.s2.options.length == 2) {
        appData.value.s2.zipCodeErrorVisible = true;
        appData.value.s2.value = '';
      }
    };
    const handleACClear = () => {
      appData.value.s2.value = '';
      appData.value.s3.value = '';
      appData.value.s4.value = '';
      appData.value.s5.value = '';
      appData.value.contact.meeting_times = '';
      appData.value.contact.meeting_times_api = '';
      appData.value.contact.meeting_times_api_ts = '';
      appData.value.queryId = '';
    };
    const handleIframeHeight = (scrollStatus = false) => {
      /* On step switch send new height */
      return setTimeout(function() {
        const htmlHeight = document.getElementsByTagName('html')[0].offsetHeight;
        parent.postMessage({ height: htmlHeight + 50, scroll: scrollStatus }, '*');
      }, stepTransitionDuration + 300);
    };
    // Gets the last step that the user has completed
    const getLastCompletedStep = () => {
      let latestCompletedStep = 0;

      // Get the latest completed step
      steps.forEach(step => {
        // Check if step data has been set
        if (appData.value[`s${step.id}`] && appData.value[`s${step.id}`].value) {
          latestCompletedStep = step.id;
        }
      });

      return latestCompletedStep;
    };
    // Handles navigating trough steps by clicking the thermometer
    const handleThermometerProgress = (stepId) => {
      if (appData.value.currentStep == stepId) {
        return;
      }

      let latestCompletedStep = getLastCompletedStep();

      // If user clicks on a step that is not yet completed then navigate the user to the latest completed step
      if (stepId > latestCompletedStep + 1) {
        // If user is submitting the step 6 form
        if (stepId === 7 && latestCompletedStep === 5) {
          appData.value.currentStep = 6;
          return handleStepProgress(stepId, appData.value.currentStep);
        }

        handleStepProgress(latestCompletedStep + 1, appData.value.currentStep);
        formErrorMessage.value =
          appData.value.currentStep === 2 ? 'Bitte geben Sie die Postleitzahl ein und wählen Sie Ihren Ort aus der Liste aus.' : 'Bitte wählen Sie eine Option aus';

        // Send data about the error and step on which the error occurred to GTM
        let gtmData = {
          'event': 'errorMessage',
          'errorMessageDetail': formErrorMessage.value,
          'step': 'Step' + appData.value.currentStep
        };
        fireGTMEvent(gtmData);
        return;
      }

      return handleStepProgress(stepId, appData.value.currentStep);
    };

    const handleStepProgress = async (stepId, previousStep = null) => {
      if (appData.value.contact.region && !appData.value.contact.region.includes('Landesverband ')) {
        localStorage.clear();
        const redirectBase = BRAND_THEME === 'default' ? '/' : `/${BRAND_THEME}`;
        window.location.replace(redirectBase);
        return;
      }

      /* On step 6 send form on next button click */
      if (stepId === 7) {
        // Validate all form fields
        await nextTick(); // Wait until the firstFormComponent is rendered
        firstFormComponent.value.validateAllFields();

        // Do not proceed if validation failed
        if (validationErrors.length > 0) {
          // Scroll to the form
          firstFormComponent.value.$el.scrollIntoView({ behavior: 'smooth' });

          // Send data about the error and step on which the error occurred to GTM
          let gtmData = {
            'event': 'errorMessage',
            'errorMessageDetail': 'Missing form fields',
            'step': 'Step' + appData.value.currentStep
          };

          fireGTMEvent(gtmData);
          return;
        }

        // If the installation address is enabled
        if (!appData.value.installation_address_checkbox && installationFormComponent.value) {
          // Validate installation address form fields
          installationFormComponent.value.validateAllFields('installation_address_');
        }

        handleIframeHeight(true);

        // User has selected that they want to book a meeting time but have not selected a meeting time
        if (appData.value.contact.termin_select && (!appData.value.s7.value || !appData.value.s7.meeting_time)) {
          formErrorMessage.value = 'Bitte füllen Sie alle Felder des Formulars aus.';

          // Send data about the error and step on which the error occurred to GTM
          let gtmData = {
            'event': 'errorMessage',
            'errorMessageDetail': formErrorMessage.value,
            'step': 'Step' + appData.value.currentStep
          };

          fireGTMEvent(gtmData);
          return;

        }

        // Do not proceed if validation failed
        if (validationErrors.length > 0) {
          // Scroll to the form
          firstFormComponent.value.$el.scrollIntoView({ behavior: 'smooth' });

          // Send data about the error and step on which the error occurred to GTM
          let gtmData = {
            'event': 'errorMessage',
            'errorMessageDetail': 'Missing form fields',
            'step': 'Step' + appData.value.currentStep
          };

          fireGTMEvent(gtmData);
          return;
        }

        // Terms and conditions check box was not checked
        if (!appData.value.agree) {
          formErrorMessage.value = 'Bitte füllen Sie alle Felder des Formulars aus.';

          // Send data about the error and step on which the error occurred to GTM
          let gtmData = {
            'event': 'errorMessage',
            'errorMessageDetail': formErrorMessage.value,
            'step': 'Step' + appData.value.currentStep
          };

          fireGTMEvent(gtmData);
          return;
        }

        // If the user has somehow selected a date from the past
        if (appData.value.s7.value && appData.value.contact.termin_select && appData.value.orderType != 'Unverbindliche Anfrage') {
          let dateString = appData.value.s7.value; // Oct 23
          let dateParts = dateString.split('.');
          let dateObject = new Date(+dateParts[2], dateParts[1] - 1, +dateParts[0], 0);
          let mydate = new Date(dateObject);
          let today = new Date();

          if (today > mydate) {
            localStorage.clear();
            const redirectBase = BRAND_THEME === 'default' ? '/' : `/${BRAND_THEME}`;
            window.location.replace(redirectBase);
            return;
          }
        }

        // If user has applied discount code double-check the discount code validity
        if (globalDiscountAvailable.value) {
          const result = await checkDiscountCodeApi(appliedDiscountCode.value);

          // If discount code is not valid remove it from localstorage & reset packages
          if (!result) {
            globalDiscountAvailable.value = false;
            appliedDiscountCode.value = '';
            localStorage.removeItem(localStorageDiscount.value);
            await getPackages();

            formErrorMessage.value = 'Der Rabattcode ist ungültig. Sie können ohne Rabatt fortfahren oder zu Schritt 5 ' +
              'zurückkehren und einen neuen Rabattcode eingeben.';

            return;
          }

        }

        formErrorMessage.value = '';

        // Send data to GTM
        handleStepGtmEvent(6, previousStep);

        // Submit the form data
        await handleFormSubmit();

        if (piwikWhiteLabels.value.includes(BRAND_THEME) && INST != 'dev') {
          sendDataToPiwik();
        }
        return;
      }

      if (
        (!appData.value[`s${appData.value.currentStep}`] || appData.value[`s${appData.value.currentStep}`].value == '') && stepId > appData.value.currentStep
      ) {
        formErrorMessage.value =
          appData.value.currentStep === 2 ? 'Bitte geben Sie die Postleitzahl ein und wählen Sie Ihren Ort aus der Liste aus.' : 'Bitte wählen Sie eine Option aus';

        // Send data about the error and step on which the error occurred to GTM
        let gtmData = {
          'event': 'errorMessage',
          'errorMessageDetail': formErrorMessage.value,
          'step': 'Step' + appData.value.currentStep
        };

        fireGTMEvent(gtmData);
        return;
      }

      formErrorMessage.value = '';

      if (stepId === 3) {
        if (typeof appData.value.s2.value !== 'object' || appData.value.s2.value.zip === '') {
          formErrorMessage.value = 'Bitte geben Sie die Postleitzahl ein und wählen Sie Ihren Ort aus der Liste aus.';
          return;
        }
      }

      // Last completed step logic
      if (appData.value.currentStep <= stepId && appData.value.currentCompletedStep <= appData.value.currentStep) {
        // Save recently completed step
        appData.value.currentCompletedStep = appData.value.currentStep;
        // Send event about completed step to piwik
        if (piwikWhiteLabels.value.includes(BRAND_THEME) && INST != 'dev') {
          sendDataToPiwik();
        }

      }

      // Go to the selected step
      appData.value.currentStep = stepId;

      // Add the step id to step history array
      if (stepHistory[stepHistory.length - 1] !== stepId) {
        stepHistory.push(stepId);
      }

      // Needed for previous step property for GTM - either 2 steps before the redirect or one step
      gtmPreviousStep = stepHistory[stepHistory.length - 3] || stepHistory[stepHistory.length - 2];

      if (previousStep !== 6) {
        // Send data to GTM
        handleStepGtmEvent(previousStep, gtmPreviousStep);
      }

      // Send current height to iframe
      handleIframeHeight(true);

      // Append current step to URL
      appendStepToUrl();

      if (isMobile.value) {
        document.querySelector('.progress').scrollIntoView({ block: 'start', behavior: 'smooth' });
      }
    };

    const handleStepGtmEvent = (stepId = null, GtmPreviousStep = null) => {
      let event = 'hnrStep' + stepId + 'Complete';
      let containsSameValues = true;
      let engagementDetail = appData.value[`s${stepId}`]?.value;
      // Step 6 has different engagement detail
      engagementDetail = (stepId === 6) ? appData.value.orderType : engagementDetail;

      if (!engagementDetail) {
        return;
      }

      let gtmData = {
        'event': event,
        'engagementDetail': engagementDetail
      };

      if (stepId === 2) {
        gtmData.engagementDetail = appData.value.contact.zip;
      }

      if (stepId === 3) {
        gtmData.engagementDetail = 'Pflegegrad: ' + engagementDetail;
      }

      if (stepId === 4) {
        gtmData.engagementDetail = 'Umgebung: ' + engagementDetail;
      }

      // Step 5 has different variables
      if (stepId === 5) {
        gtmData = {
          'event': event,
          'engagementDetail': appData.value.orderType,
          'hnrVariant': appData.value.s5.value
        };
      }

      // Step 6 has different variables
      if (stepId === 6) {
        gtmData = {
          'event': event,
          'engagementDetail': engagementDetail,
          'discoveryDetail': appData.value.erfahren,
          'landesverband': appData.value.contact.region,
          'previousStep': 'Step ' + GtmPreviousStep,
          'hnrVariant': appData.value.s5.value
        };
        // Send data to GTM
        fireGTMEvent(gtmData);
      }

      // Check if stepGtmValues already contains an object with the same values as gtmData
      // This is needed not to spam events but only send when/if a value has changed
      if (appData.value[`s${stepId}`].hasOwnProperty('stepGtmValues')) {
        containsSameValues = appData.value[`s${stepId}`].stepGtmValues.some(obj =>
          Object.keys(obj).every(key => obj[key] === gtmData[key])
        );
      }

      // If no such object exists, push gtmData into stepGtmValues
      if (!containsSameValues) {
        let gtmDataCopy = { ...gtmData };
        appData.value[`s${stepId}`].stepGtmValues.push(gtmDataCopy);

        // Prevent sending more than 1 user answer
        if (appData.value[`s${stepId}`].stepGtmValues.length > 1) {
          appData.value[`s${stepId}`].stepGtmValues.shift();
        }

        gtmData.previousStep = 'Step ' + GtmPreviousStep;

        // Exceptional case for step 1 if no previous step has yet been completed and user completes step 1
        if (getLastCompletedStep() === 1 && (stepId === 1 && previousStep.value === 1)) {
          gtmData.previousStep = 'NoStep';
        }

        // Send data to GTM
        fireGTMEvent(gtmData);
      }
    };

    const fireGTMEvent = (params = {}) => {
      if (gtmWhiteLabels.value.includes(BRAND_THEME) && INST != 'dev') {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(params);
      }
    };

    // Returns a string with packages separated by a comma
    const packageList = () => {
      return Object.keys(appData.value.appPackages.packages).join(', ');
    };

    const userSource = () => {
      appData.value.usersource = decodeURI(queryObject.usersource ?? appData.value.usersource);
      appData.value.cid = queryObject.cid ?? appData.value.cid;
    };
    // Handle meeting time intervals & exceptions for step 6 datepicker
    const getIntervalExceptions = () => {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      d.setHours(0, 0, 0, 0);

      let dayTypeDBtoJavascript = {
        0: 1,
        1: 2,
        2: 3,
        3: 4,
        4: 5,
        5: 6,
        6: 0
      };

      let intervals = [];
      let exceptions = [];
      let intervalExceptions = [];

      const seenKeys = new Set();

      for (let y = 0; y <= 62; y++) {
        for (let i = 0; i < appData.value.contact.meeting_times.length; i++) {
          if (dayTypeDBtoJavascript[appData.value.contact.meeting_times[i].day] === d.getDay()) {

            const date = String(d.getDate()).padStart(2, '0') + '.' + (String(d.getMonth() + 1).padStart(2, '0')) + '.' + d.getFullYear();
            const startTime = appData.value.contact.meeting_times[i].open_from;
            const endTime = appData.value.contact.meeting_times[i].open_to;
            const capacity = appData.value.contact.meeting_times[i].capacity;

            const key = `${date}|${startTime}|${endTime}|${capacity}`;

            if (!seenKeys.has(key)) {

              if (capacity > 0) {
                intervals.push({
                  date: date,
                  startTime: startTime,
                  endTime: endTime,
                  capacity: capacity
                });
              }

              // Separate intervals without capacity. We do not send this data via API
              if (capacity <= 0) {
                intervalsNotForApi.value.push({
                  date: date,
                  startTime: startTime,
                  endTime: endTime,
                  capacity: capacity
                });
              }

              seenKeys.add(key);
            }

          }
        }
        d.setDate(d.getDate() + 1);
      }

      // Extract exceptions from meeting times
      for (let i = 0; i < appData.value.contact.meeting_times.length; i++) {
        appData.value.contact.meeting_times[i].exceptions?.forEach(exception => {
          // Create separate array for exception dates
          exceptions.push([
            exception.from_date,
            exception.to_date,
            appData.value.contact.meeting_times[i].open_from,
            appData.value.contact.meeting_times[i].open_to,
            exception.capacity
          ]);
        });
      }

      // Calculate the difference between start date and end date & add dates that are in between them to exceptions
      const seenExceptionKeys = new Set();

      exceptions.forEach(dateRange => {
        const [startDate, endDate, openFrom, openTo, capacity] = dateRange;
        // use a copy so we don't mutate original date values elsewhere
        let currentDate = new Date(startDate);
        const parsedEndDate = new Date(endDate);

        // loop including endDate
        while (currentDate <= parsedEndDate) {
          // Format the date as DD.MM.YYYY using UTC getters
          const day = String(currentDate.getUTCDate()).padStart(2, '0');
          const month = String(currentDate.getUTCMonth() + 1).padStart(2, '0');
          const year = currentDate.getUTCFullYear();

          const formattedDate = `${day}.${month}.${year}`;
          const key = `${formattedDate}|${openFrom}|${openTo}|${capacity}`;

          if (!seenExceptionKeys.has(key)) {
            let data = {
              'date': formattedDate,
              'startTime': openFrom,
              'endTime': openTo,
              'capacity': capacity
            };

            if (capacity > 0) {
              intervalExceptions.push(data);
            } else {
              // Separate exceptions without capacity. We do not send this data via API
              intervalExceptionsNotForApi.value.push(data);
            }

            seenExceptionKeys.add(key);
          }

          // advance exactly 7 days (use UTC to match the UTC getters above)
          currentDate.setUTCDate(currentDate.getUTCDate() + 7);
        }
      });

      // Merge intervals and exceptions, do not create duplicates
      intervals.forEach(interval => {
        const exists = intervalExceptions.some(exception =>
          exception.date === interval.date &&
          exception.startTime === interval.startTime &&
          exception.endTime === interval.endTime
        );

        // Only push to intervalExceptions if the interval does not already exist
        if (!exists) {
          intervalExceptions.push(interval);
        }
      });

      return JSON.stringify(intervalExceptions);
    };

    /***********************************************************************************************************
    /**************************************** onBeforeMount ***************************************************
    /**********************************************************************************************************/

    onBeforeMount(() => {
      if ('clearcache' in queryObject && queryObject.clearcache === 'true') {
        localStorage.clear();
      }

      // Check if the current user has discount code applied
      if (localStorage.getItem(localStorageDiscount.value)) {
        globalDiscountAvailable.value = true;
      }

    });

    /***********************************************************************************************************
    /**************************************** onMounted *******************************************************
    /**********************************************************************************************************/

    onMounted(() => {

      // Attach event listener to window resize event
      window.addEventListener('resize', () => {
        windowWidth.value = window.innerWidth;
      });

      // If user is starting first step send the event to GTM
      if (appData.value.currentStep == 1 && (appData.value.currentCompletedStep == 1 && getLastCompletedStep() == 0)) {
        fireGTMEvent({'event': 'hnrStart'})
      }

      handleIframeHeight();
    });

    /***********************************************************************************************************
    /**************************************** WATCHERS *********************************************************
    /**********************************************************************************************************/

    watch(globalDiscountAvailable, (newVal, oldVal) => {
      if (newVal) {
        appliedDiscountCode.value = JSON.parse(localStorage.getItem(localStorageDiscount.value));
      }
    });

    watch(
      () => appData.value.s3.value,
      (newVal, oldVal) => {
        if (INST == 'dev') {
          // this.appData.value.contact.meeting_times_api = [ {"date": "29.11.2024", "startTime": "14:00", "endTime": "16:00", "options": 1, "available": 1},
          //   {"date": "30.11.2024", "startTime": "14:00", "endTime": "16:00", "options": 1, "available": 1},
          //   {"date": "1.12.2024", "startTime": "14:00", "endTime": "16:00", "options": 1, "available": 1},
          //   {"date": "2.12.2024", "startTime": "14:00", "endTime": "16:00", "options": 1, "available": 1},
          //   {"date": "13.06.2024", "startTime": "14:00", "endTime": "16:00", "options": 1, "available": 1},
          //   {"date": "14.06.2024", "startTime": "14:00", "endTime": "16:00", "options": 1, "available": 1},
          //   {"date": "15.06.2024", "startTime": "14:00", "endTime": "16:00", "options": 1, "available": 1},
          //   {"date": "19.06.2024", "startTime": "14:00", "endTime": "16:00", "options": 1, "available": 1},
          // ]
          appData.value.queryId = '33111333-777555';
        }
        getPackages();
        if (oldVal) {
          // Reset user selected package after re-fetching packages from API
          appData.value.s5.value = '';
          appData.value.s5.userSelectedPrice = 0;
        }
      }
    );

    watch(() => appData.value.s4.value, (newVal, oldVal) => {
      getPackages();
      if (newVal !== 'Zuhause') { // Partnertarif is only available if user selected 'Zuhause'
        appData.value.partnertarif = false;
      }
      if (oldVal) {
        // Reset user selected package after re-fetching packages from API
        appData.value.s5.value = '';
        appData.value.s5.userSelectedPrice = 0;
      }
    });

    watch(
      () => appData.value.installation_address_checkbox,
      (newValue) => {
        if (newValue === true) {
          appData.value.contact.installation_address_anrede = '';
          appData.value.contact.installation_address_email = '';
          appData.value.contact.installation_address_zipOfContact = '';
          appData.value.contact.installation_address_firstname = '';
          appData.value.contact.installation_address_lastname = '';
          appData.value.contact.installation_address_house_number = '';
          appData.value.contact.installation_address_phone = '';
          appData.value.contact.installation_address_phone_prefix = '';
          appData.value.contact.installation_address_stadt = '';
          appData.value.contact.installation_address_street = '';
        }
        handleIframeHeight();
      }
    );

    // Update prices whenever any reactive dependency changes
    watchEffect(() => {
      packagePricesDiscounts.value = packagePriceStore.updatePrices(
        BRAND_THEME,
        appData.value
      )
    });

    return {
      packagePriceStore,
      firstFormComponent,
      installationFormComponent,
      dateTimePickerComponent,
      landingPageWhiteLabels,
      globalDiscountAvailable,
      stepHistory,
      gtmPreviousStep,
      windowWidth,
      queryObject,
      stepTransitionDuration,
      app_theme_brand,
      app_title,
      custom_brand,
      appPackages,
      packagePricesDiscounts,
      localStorageDiscount,
      appliedDiscountCode,
      previousStep,
      steps,
      meetingTimeSteps,
      tooltipContent,
      tooltipContentWl,
      filteredAppRadios,
      appData,
      formErrorMessage,
      validationErrors,
      isTabletPortrait,
      isMobile,
      handleFormSubmit,
      getDiscountData,
      getPotentialServices,
      sendDataToPiwik,
      appendStepToUrl,
      mapAnswerValues,
      handleValidationErrors,
      clearDatepicker,
      pushToDataLayer,
      reverseAppPackages,
      getPackages,
      handleZipInput,
      handleZipFocus,
      handleACChange,
      handleACSelect,
      handleACClose,
      handleACClear,
      handleIframeHeight,
      getLastCompletedStep,
      handleStepProgress,
      handleStepGtmEvent,
      handleThermometerProgress,
      fireGTMEvent,
      packageList,
      userSource
    };

  }
}

</script>

<template>
  <div id="root" :class="app_theme_brand" class="root">
    <HeaderComponent v-if="!isMobile && landingPageWhiteLabels.includes(app_theme_brand)" />
    <MobileHeaderComponent v-if="isMobile && landingPageWhiteLabels.includes(app_theme_brand)" />
    <div class="root__body">
      <div v-if="!isMobile && landingPageWhiteLabels.includes(app_theme_brand)" class="back">
        <a v-if="app_theme_brand == 'hausnotruf-testen'" href="https://www.johanniter.de/dienste-leistungen/pflege-und-begleitung/hausnotruf-testen/" class="back-to-home">&lt; Zur Webseite</a>
        <a v-else href="https://www.johanniter.de/dienste-leistungen/pflege-und-begleitung/hausnotruf/" class="back-to-home">&lt; Zur Webseite</a>
      </div>
      <HeaderLogo :app-theme-brand="app_theme_brand" :app-title="app_title" />
      <div class="heading">
      <h1> Fragen Sie jetzt den passenden Hausnotruf an </h1>
      </div>
      <div class="progress">
        <button
          v-for="(step, index) in steps"
          :key="index"
          :class="{active: appData.currentStep === step.id, 'icon icon--check': step.id === steps.length}"
          class="progress__btn"
          @click="handleThermometerProgress(step.id)"
        >
          <span v-if="step.id !== steps.length">{{ step.id }}</span>
        </button>
      </div>
      <main
        ref="mainstepper"
        id="stepper"
        :class="'stepper-' + appData.currentStep"
        class="stepper"
      >
        <Transition
          :duration="stepTransitionDuration"
          mode="out-in"
        >
          <div class="step-wrapper" v-if="appData.currentStep === 1">
            <h4 class="stepper__title">
              Für wen suchen Sie einen Notrufdienst?
            </h4>
            <div
              v-tooltip="{html: true, content: app_theme_brand === 'default' ? tooltipContent.s1 : tooltipContentWl.s1}"
              class="icon icon--help"
            />

            <InputChoice
              :value="appData.s1.value"
              :options="appData.s1.options"
              step="s1"
              @map-answer-values="mapAnswerValues"
            />
          </div>
        </Transition>

        <Transition
          :duration="stepTransitionDuration"
          mode="out-in"
        >
          <div class="step-wrapper step2" v-if="appData.currentStep === 2">
            <h4 class="stepper__title">In welcher Region wird der Notrufdienst benötigt?</h4>
            <div v-tooltip="{html: true, content: app_theme_brand == 'default' ? tooltipContent.s2 : tooltipContentWl.s2}"
                 class="icon icon--help" />

            <div class="zip-autocomplete" @input.capture="handleZipInput" @focusin="handleZipFocus">

              <Multiselect
                v-model="appData.s2.value"
                id="zip-autocomplete"
                name="zip-autocomplete"
                input-type="text"
                autocomplete="off"
                :placeholder="app_theme_brand === 'pflegemarkt' ? '' : 'Hier Postleitzahl eingeben'"
                :searchable="true"
                :options="appData.s2.options"
                no-options-text="Bitte geben Sie eine Postleitzahl ein"
                :resolve-on-load="false"
                :filterResults="false"
                track-by="value.id"
                :object="true"
                @search-change="handleACChange"
                @select="handleACSelect"
                @close="handleACClose"
                @clear="handleACClear"
              >
                <template v-slot:singlelabel="{ value }">
                  <div class="multiselect-single-label">
                    <span> {{ value.value.zip }} </span>
                  </div>
                </template>

                <template v-slot:option="{ option }">
                  <span><strong> {{ option.value?.city }} </strong></span>
                  <span> {{ option.value?.zip }} </span>
                </template>

              </Multiselect>

              <span
                v-if="appData.s2.zipCodeErrorVisible"
                class="icon icon--sort-up help is-danger">Zu Ihrer Postleitzahl gibt es mehr als einen Ort. Bitte wählen Sie einen Ort aus der Liste aus.
              </span>

            </div>
          </div>
        </Transition>

        <Transition
          :duration="stepTransitionDuration"
          mode="out-in"
        >
          <div class="step-wrapper" v-if="appData.currentStep === 3">
            <h4 class="stepper__title">Liegt ein Pflegegrad vor?</h4>
            <div v-tooltip="{html: true, content: app_theme_brand == 'default' ? tooltipContent.s3 : tooltipContentWl.s3}"
                 class="icon icon--help" />
            <InputChoice
              :value="appData.s3.value"
              :options="appData.s3.options"
              step="s3"
              @map-answer-values="mapAnswerValues"
            />
          </div>
        </Transition>
        <Transition
          :duration="stepTransitionDuration"
          mode="out-in"
        >
          <div class="step-wrapper" v-if="appData.currentStep === 4">
            <h4 class="stepper__title">In welcher Umgebung soll der Notrufdienst genutzt werden?</h4>
            <div v-tooltip="{html: true, content: app_theme_brand == 'default' ? tooltipContent.s4 : tooltipContentWl.s4}"
                 class="icon icon--help" />
            <InputChoice
              :value="appData.s4.value"
              :options="appData.s4.options"
              step="s4"
              @map-answer-values="mapAnswerValues"
            />
          </div>
        </Transition>

        <Transition
          :duration="stepTransitionDuration"
          mode="out-in"
        >
          <div class="step-wrapper" v-if="appData.currentStep === 5">
            <h4 class="stepper__title">
              Unsere optimalen Lösungen aufgrund Ihrer vorherigen Auswahl:
              <span>
                {{ appData.s4.value === 'Zuhause und unterwegs' ? 'Hausnotruf für Zuhause und unterwegs' : 'Der Johanniter-Hausnotruf' }}
              </span>
            </h4>

            <div class="service-table">
              <div class="result_table_wrap table_wrap">

                <ServiceTable
                  v-if="!isMobile && !isTabletPortrait"
                  :app-data-s3-value="appData.s3.value"
                  :app-theme-brand="app_theme_brand"
                  :tooltip-content="tooltipContent"
                  :tooltip-content-wl="tooltipContentWl"
                  :pre-calculated-app-package-data="packagePricesDiscounts"
                />

                <ServiceTableMobile
                  v-if="isMobile && !isTabletPortrait"
                  :app-data-s3-value="appData.s3.value"
                  :app-theme-brand="app_theme_brand"
                  :tooltip-content="tooltipContent"
                  :tooltip-content-wl="tooltipContentWl"
                  :pre-calculated-app-package-data="packagePricesDiscounts"
                />

                <ServiceTableTablet
                  v-if="isTabletPortrait"
                  :app-data-s3-value="appData.s3.value"
                  :app-theme-brand="app_theme_brand"
                  :tooltip-content="tooltipContent"
                  :tooltip-content-wl="tooltipContentWl"
                  :pre-calculated-app-package-data="packagePricesDiscounts"
                />

              </div>
              <div v-if="appData.s4.value == 'Zuhause'" class="partnertarif-wrapper">
                <h4>Zubuchbare Optionen</h4>
                <table>
                  <tbody>
                    <tr>
                      <td style="background: transparent;"></td>
                      <td>Einzelpreis (monatlich)</td>
                      <td>Hinzufügen?</td>
                    </tr>
                    <tr>
                      <td>
                        Partnertarif
                        <div
                          v-tooltip="{html: true, content: tooltipContent.s5PartnerTartif}"
                          class="icon icon--help-second"
                        />
                      </td>
                      <td><strong>10,00 €</strong></td>
                      <td>
                        <div>
                          <Toggle v-model="appData.partnertarif" class="toggle-blue" />
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="table-info-title">
                * Bei im Einzelfall festgestellter Notwendigkeit der Versorgung mit einem Hausnotrufsystem übernimmt die Pflegekasse nach Antrag des Kunden die Kosten der Basisleistung. <br>
                <div v-if="app_theme_brand == 'harz-energie' || app_theme_brand == 'hanova' || app_theme_brand == 'enercity' || app_theme_brand == 'meravis'">
                  Einmalige Anschlussgebühr entfällt für unsere Kunden. <br>
                </div>
              </div>
              <div class="table-info-title">
                * Bei anerkannter Pflegebedürftigkeit ist eine Kostenübernahme in Höhe des aktuellen Pflegehilfsmittel-Zuschusses möglich.
              </div>

              <div class="service-order-selection">
                <h4>Wählen Sie eine Option und klicken Sie anschließend auf „Weiter“</h4>
                <OnlineOrder></OnlineOrder>
              </div>
            </div>
          </div>
        </Transition>
        <Transition
          :duration="stepTransitionDuration"
          mode="out-in"
        >
          <div class="step-wrapper" v-if="appData.currentStep === 6">
            <div class="order-selection">
              <OnlineOrder></OnlineOrder>
            </div>

            <div>
              <h4 class="stepper__title">Sie haben Ihre optimale Lösung gefunden? <br><span class="custom-pm-h4">Bitte geben Sie hier Ihre Daten ein und wir nehmen umgehend Kontakt mit Ihnen auf.</span></h4>
              <h6 class="sub-title">Kontaktinformationen</h6>
            </div>
            <FormComponent
              ref="firstFormComponent"
              form-id="mailform"
              :app-theme-brand="app_theme_brand"
              @validation-updated="handleValidationErrors"
            />
            <div>
              <div class="stepper__input termin_select_checkbox">
                <div v-if="appData.orderType != 'Unverbindliche Anfrage'" class="installationadresse-wrapper">
                  <div class="stepper__input stepper__input-installationsadresse">
                    <h6 class="sub-title">Installationsadresse</h6>
                    <Toggle v-model="appData.installation_address_checkbox" class="toggle-blue" />
                    <label class="checkbox-title switch-label"><h6>Die Installationsadresse entspricht der Adresse der Kontaktinformationen</h6></label>
                  </div>
                  <div>
                    <div v-if="!appData.installation_address_checkbox">
                      <FormComponent
                        ref="installationFormComponent"
                        form-type="installation_address_"
                        :app-theme-brand="app_theme_brand"
                        @validation-updated="handleValidationErrors"
                      />
                    </div>
                  </div>
                </div>
                <DateTimePickerComponent
                  v-if="appData.orderType !== 'Unverbindliche Anfrage'"
                  ref="dateTimePickerComponent"
                  :handle-iframe-height="handleIframeHeight"
                />
              </div>
              <div v-if="filteredAppRadios" class="second-column custom-select">
                <div class="stepper__input"><label class="checkbox-title">Wie haben Sie vom Johanniter-Hausnotruf erfahren?</label></div>
                <div class="stepper__input">
                  <div class="field">
                    <select
                      v-model.lazy="appData.erfahren"
                      :class="{'input': true, 'is-danger': validationErrors.includes('contact_second_pflegedienst')}"
                      name="radio-erfahren"
                    >
                      <option disabled value="">Bitte auswählen</option>
                      <option v-for="option in filteredAppRadios" :key="option" :value="option">
                        {{ option }}
                      </option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="stepper__input terms-and-conditions">
                <div class="checkbox-component">
                  <input id="app-agree" v-model="appData.agree" type="checkbox" name="Datenschutz" value="">
                  <label for="app-agree">
                    <span class="input-box">
                      <svg viewBox="0 0 16 16" class="input-box-tick">
                        <path :class="{ 'no-stroke': !appData.agree }" fill="none" d="M1.7,7.8l3.8,3.4l9-8.8"></path>
                      </svg>
                    </span> Ja, ich habe die <a href="https://www.johanniter.de/datenschutz/" target="_blank">Datenschutzerklärung</a>
                    zur Kenntnis genommen und bin mit der Verarbeitung meiner Daten im Rahmen dieser Anfrage einverstanden
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Transition>

        <div v-show="formErrorMessage"
             class="error-message-global">{{ formErrorMessage }}</div>
        <div class="nav">
          <button v-show="appData.currentStep > 1 && appData.currentStep <= 7"
                  ref="stepBackwards"
                  class="button button--backwards"
                  @click="handleStepProgress(appData.currentStep - 1, appData.currentStep);">Zurück
          </button>
          <button v-show="appData.currentStep >= 1 && appData.currentStep < 6"
                  ref="stepForwards"
                  class="button button--forwards"
                  @click="handleStepProgress(appData.currentStep + 1, appData.currentStep)">Weiter
          </button>
          <button v-show="appData.currentStep == 6"
                  ref="stepForwards"
                  class="button button--forwards button--forwards-last"
                  @click="handleStepProgress(appData.currentStep + 1)">
            <span v-if="appData.orderType == 'Online Bestellung'">Online bestellen</span>
            <span v-else>Unverbindlich anfragen</span>
          </button>
        </div>
      </main>
      <div v-show="app_theme_brand != 'default'" class="footer">Ein Service der Johanniter-Unfall-Hilfe e. V. - <a href="https://www.johanniter.de/datenschutz/" target="_blank">Datenschutzerklärung</a></div>
    </div>
    <StatisticsComponent v-if="landingPageWhiteLabels.includes(app_theme_brand)" />
    <FooterComponent v-if="landingPageWhiteLabels.includes(app_theme_brand)" />
  </div>
</template>

<style lang="scss">
  div.heading {
    margin-top: 20px;
    text-align: center;
    // Fonts from the https://www.johanniter.de/dienste-leistungen/pflege-und-begleitung/hausnotruf/
    font-family: "MavenProBold", Arial, sans-serif;
    font-size: 2rem;
    line-height: 1.15;
    color: #000548;
    -webkit-hyphens: auto;
    -ms-hyphens: auto;
    hyphens: auto;
    overflow-wrap: break-word;

    @media (max-width: 600px) {
      margin-top: 100px;
      font-size: 1.25rem;
    }
  }
  .step-wrapper {
    padding: 3rem;
  }
  .toggle-container .toggle {
    width: 35px;
  }
  .toggle-container .toggle-handle {
    width: 20px;
    height: 20px;
    top: auto;
  }
  .v-enter-active,
  .v-leave-active {
    transition: opacity 0.5s ease;
  }
  .v-enter-from,
  .v-leave-to {
    opacity: 0;
  }
  .back a {
    text-decoration: none;
    color: #000548;
    font-size: 14px;
  }
  .terms-and-conditions {
    display: flex;
  }
  .terms-and-conditions #app-agree {
    display: none;
  }
  .terms-and-conditions label {
    font-size: 14px;
  }
  .checkbox-component .input-box {
    display: inline-block;
    border: 1px solid #000;
    border-radius: 14%;
    background: #fff;
    overflow: hidden;
    vertical-align: -5%;
    user-select: none;
  }
  .checkbox-component .input-box .no-stroke {
    stroke: none !important;
  }
</style>
